// Plan 14-05 (MUI-06) — FriendInviteModal.
//
// End-of-3rd-real-match modal per DEC-027 step 5. Distinct from the existing
// FriendInviteSuggestion (which fires post-tutorial); shares the same modal
// frame recipe (Pattern S1 in 14-PATTERNS.md).
//
// One-shot semantics:
//   • Server flag `users.onboarding_flags.friendInviteShown` is the
//     authoritative idempotency gate. Both CTAs ("Share my code" / "Maybe
//     later") POST `/api/onboarding/flag {flag:'friendInviteShown'}` — once
//     the modal has been shown AND the user has made a choice, the server
//     records it.
//   • Flag-POST failures do NOT block dismissal (the server is idempotent;
//     the next opportunity will re-set the flag). The local UI is dismissed
//     either way to avoid trapping the user.
//   • The MatchScreen is responsible for the gating logic
//     (realMatchesPlayed === 3 → mount with open=true). This component is
//     purely the modal surface + the flag POST.
//
// No useEffect-driven auto-fire on mount — the flag is only set on user
// action (Share / Maybe later). This preserves the "user saw it AND chose"
// contract; a render-once-then-crash before user interaction does not burn
// the flag.

import { type JSX, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'

import { apiFetch } from '../../net/client'
import { Dialog } from '../components/Dialog'

export interface FriendInviteModalProps {
  readonly open: boolean
  readonly onDismiss: () => void
  readonly onShare: () => void
}

async function postFriendInviteFlag(): Promise<void> {
  try {
    await apiFetch('/api/onboarding/flag', 'POST', {
      body: { flag: 'friendInviteShown' },
    })
  } catch {
    // Flag POST is best-effort; the server is idempotent so a subsequent
    // opportunity will retry. Do NOT throw — the local dismiss must
    // proceed regardless (we don't want to trap the user behind a
    // transient network error).
  }
}

export function FriendInviteModal({
  open,
  onDismiss,
  onShare,
}: FriendInviteModalProps): JSX.Element | null {
  // ARCA-82 WS2 — useFocusTrap + scrim/panel styling live in <Dialog>/ModalScrim.

  const handleShare = useCallback(async () => {
    await postFriendInviteFlag()
    onShare()
  }, [onShare])

  const handleLater = useCallback(async () => {
    await postFriendInviteFlag()
    onDismiss()
  }, [onDismiss])

  if (!open) return null

  return (
    // Center-dialog flavor (ARCA-82 WS2): scrim + popIn + focus trap +
    // dismissal live in <Dialog>/ModalScrim. padding:0 so the deep header
    // band bleeds to the rounded panel edge (overflow:hidden clips it).
    <Dialog
      onClose={onDismiss}
      testId="friend-invite-modal"
      labelledBy="friend-invite-modal-title"
      panelStyle={{ padding: 0, overflow: 'hidden' }}
    >
      <div
        style={{
          height: '48px',
          backgroundColor: 'var(--color-surface-deep)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <h2
          id="friend-invite-modal-title"
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: '18px',
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage
            id="match.friend_invite.modal.title"
            defaultMessage="Three matches in. Got a friend who'd play?"
          />
        </h2>
      </div>
      <div style={{ padding: '20px' }}>
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-body)',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: 1.5,
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage
            id="match.friend_invite.modal.body"
            defaultMessage="Share your code. Same court, two minds."
          />
        </p>
        <div
          style={{
            marginTop: '20px',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={handleLater}
            data-cta="friend-invite-modal-later"
            style={{
              height: '48px',
              padding: '0 20px',
              backgroundColor: 'transparent',
              border: '2px solid var(--color-surface-stroke)',
              borderRadius: '12px',
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: '16px',
              color: 'var(--color-text-primary)',
              cursor: 'pointer',
            }}
          >
            <FormattedMessage
              id="match.friend_invite.modal.cta_dismiss"
              defaultMessage="Maybe later"
            />
          </button>
          <button
            type="button"
            onClick={handleShare}
            data-cta="friend-invite-modal-share"
            style={{
              height: '48px',
              padding: '0 24px',
              backgroundColor: 'var(--color-cta-primary)',
              border: '3px solid var(--color-text-stroke)',
              borderRadius: '12px',
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: '16px',
              color: 'var(--color-inkwell)',
              cursor: 'pointer',
              boxShadow: '0 6px 0 var(--color-surface-deep)',
            }}
          >
            <FormattedMessage
              id="match.friend_invite.modal.cta"
              defaultMessage="Share my code"
            />
          </button>
        </div>
      </div>
    </Dialog>
  )
}
